import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDeletedInquiries, setInquiryId } from "../../../store/inquiry-store/inquiryAction";
import { IInquiry, IModifiedBy } from "../../../interfaces";
import RecoverDeletedInquiry from "../recover-delete";
import PermanentDeleteInquiry from "../permanent-delete";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import paginationFactory from "react-bootstrap-table2-paginator";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { translation } from "../../../locales/en-US/translation.json";

const DeletedInquiryList: React.FC = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const state = useSelector((state) => state.inquiryReducer);
	const [inquiries, setInquiries] = useState<IInquiry[]>([]);
	const userState = useSelector((userState) => userState.userReducer);
	const [permission, setPermission] = useState<string>("");

	// Table confuguration
	const { SearchBar } = Search;
	const options = {
		paginationSize: 4,
		pageStartIndex: 1,
		sizePerPage: 15,
		hideSizePerPage: true,
		alwaysShowAllBtns: true,
	};

	// Fetch inquiry information
	useEffect(() => {
		dispatch(getDeletedInquiries());
	}, [dispatch, getDeletedInquiries]);

	useEffect(() => {
		setInquiries(state.deleteInquiries);
	}, [state.deleteInquiries, setInquiries]);

	useEffect(() => {
		if (userState.authUser && userState.authUser.permissionLevel) {
			setPermission(userState.authUser.permissionLevel);
		}
	}, [userState.authUser, setPermission]);

	// Table column configurations
	const tableColumnData = [
		{
			dataField: "actions",
			text: translation.table["table-action-header"],
			formatter: (cell: any, row: IInquiry) => actionButtonFormatter(row),
			headerStyle: { width: "90px" },
		},
		{ dataField: "name", text: translation.table["table-title-header"], headerStyle: { width: "200px" } },
		{ dataField: "email", text: translation.table["table-email-header"], headerStyle: { width: "200px" } },
		{ dataField: "message", text: translation.table["table-message-header"], headerStyle: { width: "200px" } },
		{
			dataField: "deletedAt",
			text: translation.table["table-deleted-at-header"],
			headerStyle: { width: "220px" },
			formatter: (cell: string) => {
				return moment(cell).format("LLL");
			},
		},
	];

	const handleSetRecoverInquiryPermanently = (inquiry: any, inquiryId: string) => {
		if (inquiry) {
			dispatch(setInquiryId(inquiryId));
			$("#inquiryRecoverDeletedModal").modal("show");
		}
	};
	const handleSetDeleteInquiryPermanently = (inquiry: any, inquiryId: string) => {
		if (inquiry) {
			dispatch(setInquiryId(inquiryId));
			$("#deleteInquiryPermanentlyModal").modal("show");
		}
	};

	// Table action buttons
	const actionButtonFormatter = (row: any) => {
		return (
			<div>
				{row ? (
					<span className="dropdown show">
						<span className="dropdown">
							<span className="btn shadow-none btn-sm" data-mdb-toggle="dropdown">
								<i className="fas fa-ellipsis-h"></i>
							</span>
							<div className="dropdown-menu dropdown-menu-right">
								{(permission === "ROOT_ADMIN" || permission === "ADMIN" || permission == "EDITOR") && (
									<button className="dropdown-item" onClick={(e) => handleSetRecoverInquiryPermanently(e, row._id)}>
										<i className="fas fa-undo" /> {translation["data-row-action-dropdown"].recover}
									</button>
								)}
								{(permission === "ROOT_ADMIN" || permission === "ADMIN") && (
									<button className="dropdown-item" onClick={(e) => handleSetDeleteInquiryPermanently(e, row._id)}>
										<i className="far fa-trash-alt" /> {translation["data-row-action-dropdown"]["delete-permanently"]}
									</button>
								)}
							</div>
						</span>
					</span>
				) : null}
			</div>
		);
	};

	const expandRow = {
		showExpandColumn: true,
		expandByColumnOnly: true,
		onlyOneExpanding: true,
		expandHeaderColumnRenderer: ({ isAnyExpands }: any) => {
			if (isAnyExpands) {
				return <b style={{ cursor: "pointer" }}></b>;
			}
			return <b style={{ cursor: "pointer" }}></b>;
		},
		expandColumnRenderer: ({ expanded }: any) => {
			if (expanded) {
				return (
					<div style={{ cursor: "pointer", marginTop: "5px" }}>
						<i className="fas fa-chevron-circle-up"></i>
					</div>
				);
			}
			return (
				<div style={{ cursor: "pointer", marginTop: "5px" }}>
					<i className="fas fa-chevron-circle-down"></i>
				</div>
			);
		},
		renderer: (row: IInquiry) => (
			<div>
				<h5>{translation["table-row-information"]["common-information"].message}</h5>
				<div className="row">
					<div className="col-md-10 col-sm-12">
						<p>{row.message}</p>
					</div>
				</div>
			</div>
		),
	};
	const handleGoBackToInquiries = (inquiry: any) => {
		if (inquiry) {
			history.push("/inquiries");
		}
	};

	return (
		<div className="card">
			<div className="row">
				<div className="col-6">
					<h3 className="page-title">{translation["page-title"]["inquiries-page-header"]}</h3>
					<p className="page-description text-muted">{translation["page-description"]["inquiries-page-description"]}</p>
				</div>
			</div>

			<div>
				<div className="d-flex">
					<button className="btn btn-sm btn-light shadow-none btn-rounded" onClick={handleGoBackToInquiries}>
						{translation["table-data-filter-label"]["go-back"]}
					</button>
				</div>
			</div>

			<ToolkitProvider keyField="_id" data={inquiries} columns={tableColumnData} search>
				{(props) => (
					<div>
						<div className="d-flex justify-content-end">
							<SearchBar {...props.searchProps} placeholder="Search Webinars" className="mb-3 search-bar" />
						</div>
						<p className="table-description text-muted">
							{translation["table-description"]["inquiries-table-description"]}
						</p>
						<BootstrapTable
							{...props.baseProps}
							pagination={paginationFactory(options)}
							expandRow={expandRow}
							bordered
							striped
							headerClasses="header-class"
							wrapperClasses="table-responsive"
							hover
							rowClasses="table-row"
						/>
					</div>
				)}
			</ToolkitProvider>
			<RecoverDeletedInquiry />
			<PermanentDeleteInquiry />
		</div>
	);
};

export default DeletedInquiryList;
